import { z } from "zod";

// Gemeinsame Datenkontrakte (CLAUDE.md §7).

export const MODEL_BUDGET_BYTES = 2_500_000;

export const Tier = z.enum(["T0", "T1", "T2"]);
export type Tier = z.infer<typeof Tier>;

export const Device = z.enum(["ios", "android", "other"]);
export type Device = z.infer<typeof Device>;

// --- Menü / Gericht ---

export const DishI18nEntrySchema = z.object({
  name: z.string().min(1),
  desc: z.string().default(""),
  ingredients: z.string().optional(),
});
export type DishI18nEntry = z.infer<typeof DishI18nEntrySchema>;

export const DishModelSchema = z.object({
  glb: z.string().min(1).optional(),
  usdz: z.string().min(1).optional(),
  poster: z.string().optional(),
});
export type DishModel = z.infer<typeof DishModelSchema>;

export const ModelStatus = z.enum(["ready", "processing", "failed"]);
export type ModelStatus = z.infer<typeof ModelStatus>;

export const DishSchema = z.object({
  id: z.string().min(1),
  i18n: z.record(z.string(), DishI18nEntrySchema),
  priceCents: z.number().int().nonnegative(),
  // Portionskosten (Wareneinsatz) in Cent — Basis für den Deckungsbeitrag der
  // Menu-Engineering-Matrix. MUSS optional bleiben: ein fehlender Wert bedeutet
  // "unbekannt", nicht 0 — sonst verfälscht er den gewichteten Gruppen-DB.
  costCents: z.number().int().nonnegative().optional(),
  // Zubereitungszeit in Minuten → DB pro Minute (Daypart-Empfehlungen).
  prepMinutes: z.number().int().positive().optional(),
  // AR-Lift-Experiment: Kontrollvariante. Ist das Modell vorhanden, wird trotzdem nur das
  // Foto (T0) gezeigt. Ermöglicht den Vergleich "mit AR" vs. "ohne AR" am selben Gericht.
  // Bewusst KEINE Zuweisung pro Sitzung: das bräuchte eine Varianten-Kennung im Event und
  // damit eine neue Dimension im eingefrorenen Feldkatalog (Recht-und-Compliance §2.2).
  // Die Zuordnung erfolgt über Zeiträume; der Wechsel steht in der Menü-Versionshistorie.
  arHidden: z.boolean().optional(),
  allergens: z.array(z.string()).default([]),
  category: z.string().optional(),
  order: z.number().int().optional(),
  model: DishModelSchema,
  modelStatus: ModelStatus.optional(),
});
export type Dish = z.infer<typeof DishSchema>;

// Frei definierbare Kategorien: Reihenfolge + i18n-Label, sonst `label`-Fallback.
export const CategorySchema = z.object({
  id: z.string().min(1),
  i18n: z.record(z.string(), z.object({ name: z.string().min(1) })).optional(),
  label: z.string().optional(),
  order: z.number().int().optional(),
});
export type Category = z.infer<typeof CategorySchema>;

// EU-LMIV-Allergene (DACH-Gastronomie-Codes A–R). Guest/Admin rendern Code→Label;
// freie Alt-Strings (z. B. "G Milch") fallen auf Rohtext zurück (kein Datenbruch).
export const ALLERGENS: Record<string, { de: string; en: string }> = {
  A: { de: "Glutenhaltiges Getreide", en: "Cereals containing gluten" },
  B: { de: "Krebstiere", en: "Crustaceans" },
  C: { de: "Eier", en: "Eggs" },
  D: { de: "Fische", en: "Fish" },
  E: { de: "Erdnüsse", en: "Peanuts" },
  F: { de: "Sojabohnen", en: "Soybeans" },
  G: { de: "Milch", en: "Milk" },
  H: { de: "Schalenfrüchte", en: "Tree nuts" },
  L: { de: "Sellerie", en: "Celery" },
  M: { de: "Senf", en: "Mustard" },
  N: { de: "Sesamsamen", en: "Sesame" },
  O: { de: "Sulfite", en: "Sulphites" },
  P: { de: "Lupinen", en: "Lupin" },
  R: { de: "Weichtiere", en: "Molluscs" },
};
export const ALLERGEN_CODES = Object.keys(ALLERGENS);

function allergenName(code: string, lang: string): string | null {
  const e = ALLERGENS[code.toUpperCase()];
  return e ? (lang.startsWith("en") ? e.en : e.de) : null;
}

// Nimmt einen Code ("G") ODER ein Alt-Format ("G Milch"); liefert Kurz-/Vollform + Code.
export function resolveAllergen(a: string, lang = "de"): { code: string | null; short: string; full: string } {
  const t = a.trim();
  if (/^[A-Za-z]$/.test(t)) {
    const name = allergenName(t, lang);
    if (name) return { code: t.toUpperCase(), short: `${t.toUpperCase()} ${name}`, full: `${t.toUpperCase()} · ${name}` };
  }
  const m = /^([A-Za-z])\s+(.*)$/.exec(t);
  if (m) {
    const c = m[1]!.toUpperCase();
    const name = allergenName(c, lang) ?? m[2]!;
    return { code: c, short: `${c} ${name}`, full: `${c} · ${name}` };
  }
  return { code: null, short: t, full: t };
}

// Farbschema pro Betrieb → CSS-Variablen zur Laufzeit. Nur Hex (kein CSS-Injection-Vektor).
const HexColor = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
export const ThemeSchema = z.object({
  bg: HexColor.optional(),
  card: HexColor.optional(),
  fg: HexColor.optional(),
  muted: HexColor.optional(),
  accent: HexColor.optional(),
  aink: HexColor.optional(),
  border: HexColor.optional(),
  mediaBg: HexColor.optional(),
  themeColor: HexColor.optional(),
  colorScheme: z.enum(["dark", "light"]).default("dark"),
});
export type Theme = z.infer<typeof ThemeSchema>;

export const MenuSchema = z.object({
  venueId: z.string().min(1),
  locations: z.array(z.string()).default([]),
  languages: z.array(z.string()).min(1),
  categories: z.array(CategorySchema).default([]),
  theme: ThemeSchema.optional(),
  dishes: z.array(DishSchema),
});
export type Menu = z.infer<typeof MenuSchema>;

// --- Verkaufsmix: echte Stückzahlen als Popularitätsachse der Menu-Engineering-Matrix ---
// "manual" (CSV/Eingabe aus der Kassen-Artikelstatistik) ist die beste Quelle: jedes
// Kassensystem kann sie exportieren, ganz ohne POS-Integration. "intent" ist nur der
// Fallback aus dem AR-Menü und in der UI als Näherung zu kennzeichnen.
export const SalesMixSource = z.enum(["manual", "pos", "intent"]);
export type SalesMixSource = z.infer<typeof SalesMixSource>;

export const SalesMixSchema = z.object({
  venueId: z.string().min(1),
  periodFrom: z.string().min(1), // ISO-Datum
  periodTo: z.string().min(1),
  source: SalesMixSource,
  units: z.record(z.string(), z.number().int().nonnegative()), // dishId → verkaufte Stück
});
export type SalesMix = z.infer<typeof SalesMixSchema>;

// --- Analytics: EIN ID-loses Aggregat pro Besuch (Stufe B) ---
//
// Bewusst KEINE Event-Kette und KEINE Kennung über die Leitung: eine im Client erzeugte,
// an den Server gesendete Kennung fällt nach EDSA-Leitlinien 2/2023 Rn 52–53 in den
// Anwendungsbereich von Art. 5(3) ePrivacy / § 25 TDDDG — unabhängig davon, ob sie nur im
// RAM lebt. Der Client zählt im Speicher und sendet beim Verlassen der Seite bzw. bei der
// Bestellabsicht ein einziges Aggregat. Herleitung: ANALYTICS-PLAN.md §7,
// FoodAR-Recht-und-Compliance.md §2.2.
//
// DER FELDKATALOG IST RECHTLICH EINGEFROREN. Jede zusätzliche Dimension ist eine neue
// rechtliche Bewertung (DSK-Orientierungshilfe Rn 89), kein Feature-Ticket. Insbesondere
// NICHT aufnehmen: Tischnummer (Join-Key zu Reservierungs-/Kassendaten), IP, exakte
// Zeitstempel, Geräte-Fingerprints, Varianten-Kennungen für A/B-Tests.

// 30-Minuten-Raster über den Tag: 0 = 00:00–00:29 … 47 = 23:30–23:59.
// Grob genug, um einzelne Gäste nicht rekonstruierbar zu machen, fein genug für
// Mittags-/Abend-Muster. Das Datum bleibt tagesgenau (kein exakter Zeitstempel).
export const BUCKETS_PER_DAY = 48;

export const VisitDishSchema = z.object({
  dish: z.string().min(1),
  views: z.number().int().nonnegative().default(0),
  detailOpens: z.number().int().nonnegative().default(0),
  arEnters: z.number().int().nonnegative().default(0),
  arDwellMs: z.number().int().nonnegative().default(0),
  cartAdds: z.number().int().nonnegative().default(0),
  cartRemoves: z.number().int().nonnegative().default(0),
  intentQty: z.number().int().nonnegative().default(0),
});
export type VisitDish = z.infer<typeof VisitDishSchema>;

export const VisitAggregateSchema = z.object({
  v: z.number().int(),
  venue: z.string().min(1),
  loc: z.string().optional(),
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // tagesgenau, kein Zeitstempel
  dayOfWeek: z.number().int().min(0).max(6),
  bucket: z.number().int().min(0).max(BUCKETS_PER_DAY - 1),
  lang: z.string().optional(),
  device: Device,
  tier: Tier,
  durationMs: z.number().int().nonnegative(),
  fallbacks: z.number().int().nonnegative().default(0), // Gerichte, die auf 2D zurückfielen
  arUnsupported: z.number().int().nonnegative().default(0),
  dishes: z.array(VisitDishSchema).default([]),
  // Gericht-IDs der Bestellabsicht — Grundlage der Warenkorb-Affinität. Kommt ohnehin
  // in EINEM Payload an, eine sitzungsübergreifende Kennung ist dafür unnötig.
  cart: z.array(z.string().min(1)).default([]),
});
export type VisitAggregate = z.infer<typeof VisitAggregateSchema>;

// Tagesschlüssel aus einem ms-Zeitstempel (UTC) — für Zeitraum-Filter im Report.
// Unterscheidet sich bewusst von timeBucket(): dort zählt die Ortszeit des Gastes,
// hier die serverseitige Umrechnung eines übergebenen Filterwerts.
export function dayOfMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

// Hilfsfunktion, damit Client und Server dieselbe Bucket-Definition benutzen.
export function timeBucket(d: Date): { day: string; dayOfWeek: number; bucket: number } {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return {
    day: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    dayOfWeek: d.getDay(),
    bucket: d.getHours() * 2 + (d.getMinutes() >= 30 ? 1 : 0),
  };
}

// --- Bestellung (Stufe 1: nur status="intent") ---

export const OrderStatus = z.enum(["intent", "sent", "confirmed"]);
export type OrderStatus = z.infer<typeof OrderStatus>;

export const OrderItemSchema = z.object({
  dish: z.string().min(1),
  qty: z.number().int().positive(),
});
export type OrderItem = z.infer<typeof OrderItemSchema>;

// Bestellfluss (Art. 6 Abs. 1 lit. b — der Gast will bedient werden). Hier darf die
// Tischnummer stehen; sie wird NICHT mit der Nutzungsstatistik verknüpft (§2.2).
// Bewusst OHNE Sitzungs-Kennung: eine Bestellung braucht keine Wiedererkennung, und ein
// ungenutztes Kennungsfeld wäre nur eine Einladung, doch wieder eines zu speichern.
export const OrderSchema = z.object({
  venue: z.string(),
  loc: z.string().optional(),
  table: z.string().optional(),
  items: z.array(OrderItemSchema).min(1),
  status: OrderStatus,
  ts: z.number().int(),
});
export type Order = z.infer<typeof OrderSchema>;
